import { useState } from 'react';
import type { SelectedProduct, AlternativeProduct } from '@nfa/types';
import { trackAffiliateClick } from '../lib/analytics';
import { getProductImageUrl } from '../lib/api';
import { getAmazonProductUrl } from '../lib/amazon';

interface ProductCardProps {
  product: SelectedProduct;
  onSwap?: (categoryId: string, alternativeProductId: string) => void;
  onToggleOwned?: (productId: string) => void;
  isOwned?: boolean;
  showAlternatives?: boolean;
  formatPrice?: (gbpValue: number) => string;
  /** Handler to view full product details */
  onViewDetails?: (productId: string) => void;
}

function ProductImagePlaceholder() {
  return (
    <div
      data-testid="product-image-placeholder"
      className="w-20 h-20 flex items-center justify-center bg-neutral-800 rounded-lg shrink-0"
    >
      <svg
        className="w-8 h-8 text-neutral-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
        />
      </svg>
    </div>
  );
}

interface ProductImageProps {
  src: string;
  alt: string;
  onError: () => void;
}

function ProductImage({ src, alt, onError }: ProductImageProps) {
  return (
    <img
      src={src}
      alt={alt}
      onError={onError}
      className="w-20 h-20 object-cover rounded-lg shrink-0 bg-neutral-800"
    />
  );
}

function formatWeight(grams: number): string {
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(1)}kg`;
  }
  return `${grams}g`;
}

function getTierBadge(tier: SelectedProduct['tier']) {
  switch (tier) {
    case 'budget':
      return (
        <span className="px-2 py-0.5 bg-green-500/10 text-green-400 text-xs rounded">
          Budget
        </span>
      );
    case 'premium':
      return (
        <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 text-xs rounded">
          Premium
        </span>
      );
    default:
      return null;
  }
}

function formatDiff(value: number, suffix: string): string {
  if (value > 0) return `+${value}${suffix}`;
  if (value < 0) return `${value}${suffix}`;
  return `0${suffix}`;
}

function AlternativeItem({
  alt,
  onSelect
}: {
  alt: AlternativeProduct;
  onSelect: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-2 px-3 bg-neutral-800/50 rounded-md hover:bg-neutral-800 transition-colors">
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">{alt.product_name}</p>
        <div className="flex items-center gap-2 text-xs">
          {getTierBadge(alt.tier)}
          <span className={alt.diff_price < 0 ? 'text-green-400' : alt.diff_price > 0 ? 'text-red-400' : 'text-neutral-500'}>
            {formatDiff(alt.diff_price, '')}
          </span>
          <span className={alt.diff_weight < 0 ? 'text-cyan-400' : alt.diff_weight > 0 ? 'text-amber-400' : 'text-neutral-500'}>
            {formatDiff(alt.diff_weight, 'g')}
          </span>
        </div>
      </div>
      <button
        onClick={onSelect}
        className="shrink-0 px-2 py-1 text-xs bg-neutral-700 hover:bg-orange-500 text-neutral-300 hover:text-black rounded transition-colors"
      >
        Swap
      </button>
    </div>
  );
}

export function ProductCard({ product, onSwap, onToggleOwned, isOwned = false, showAlternatives = true, formatPrice, onViewDetails }: ProductCardProps) {
  // Default price formatter if none provided
  const displayPrice = formatPrice || ((gbp: number) => `£${gbp}`);
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const hasAlternatives = product.alternatives && product.alternatives.length > 0;

  // Determine if we should show the image or placeholder
  // Prefer Cloudflare Images CDN, fall back to external URL proxy
  const imageUrl = getProductImageUrl(product.cf_image_id, product.image_url);
  const hasValidImage = imageUrl && !imageError;
  const imageAlt = product.image_alt || product.product_name;

  const handleToggleOwned = () => {
    if (onToggleOwned) {
      onToggleOwned(product.product_id);
    }
  };

  // Determine primary link and retailer
  // For Amazon, ensure the affiliate tag is always present
  const { primaryLink, retailer } = (() => {
    const amazonUrl = getAmazonProductUrl(product.affiliate_links);
    if (amazonUrl) return { primaryLink: amazonUrl, retailer: 'amazon' };
    if (product.affiliate_links?.cotswold) return { primaryLink: product.affiliate_links.cotswold, retailer: 'cotswold' };
    if (product.affiliate_links?.alpkit) return { primaryLink: product.affiliate_links.alpkit, retailer: 'alpkit' };
    if (product.affiliate_links?.decathlon) return { primaryLink: product.affiliate_links.decathlon, retailer: 'decathlon' };
    return { primaryLink: null, retailer: '' };
  })();

  const handleSwap = (alternativeProductId: string) => {
    if (onSwap) {
      onSwap(product.category_id, alternativeProductId);
    }
  };

  const handleBuyClick = () => {
    trackAffiliateClick(product.product_id, retailer);
  };

  return (
    <div
      data-testid="product-card"
      className={`bg-neutral-900 border rounded-lg p-5 transition-colors group ${
      isOwned
        ? 'border-green-500/30 bg-green-500/5'
        : 'border-neutral-800 hover:border-neutral-700'
    }`}>
      <div className="flex justify-between items-start gap-4">
        {/* Product Image */}
        {hasValidImage ? (
          <ProductImage
            src={imageUrl}
            alt={imageAlt}
            onError={() => setImageError(true)}
          />
        ) : (
          <ProductImagePlaceholder />
        )}

        {/* Category & Name */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-neutral-500 uppercase tracking-wider">
              {product.category_name}
            </span>
            {getTierBadge(product.tier)}
            {product.style === 'ultralight' && (
              <span className="px-2 py-0.5 bg-cyan-500/10 text-cyan-400 text-xs rounded">
                UL
              </span>
            )}
            {isOwned && (
              <span className="px-2 py-0.5 bg-green-500/10 text-green-400 text-xs rounded">
                Owned
              </span>
            )}
          </div>
          <h3 className="font-semibold text-white text-lg truncate">
            {product.product_name}
          </h3>
        </div>

        {/* Price & Weight */}
        <div className="text-right shrink-0">
          <p className="font-mono font-bold text-xl text-white">
            {displayPrice(product.price_gbp)}
          </p>
          <p className="font-mono text-sm text-neutral-500">
            {formatWeight(product.weight_g)}
          </p>
        </div>
      </div>

      {/* Why */}
      <p className="mt-3 text-neutral-400 text-sm leading-relaxed">
        {product.why}
      </p>

      {/* Actions Row */}
      <div className="mt-4 pt-4 border-t border-neutral-800 flex flex-wrap items-center gap-2">
        {/* View Details Button */}
        {onViewDetails && (
          <button
            onClick={() => onViewDetails(product.product_id)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 hover:text-white text-sm font-medium rounded-md transition-all duration-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Details</span>
          </button>
        )}

        {/* Buy Button */}
        {primaryLink && !isOwned && (
          <a
            href={primaryLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleBuyClick}
            className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-800 hover:bg-orange-500 text-neutral-300 hover:text-black text-sm font-medium rounded-md transition-all duration-200"
          >
            <span>Buy</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}

        {/* Own Toggle Button */}
        {onToggleOwned && (
          <button
            onClick={handleToggleOwned}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
              isOwned
                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                : 'bg-neutral-800 text-neutral-400 hover:text-white hover:bg-neutral-700'
            }`}
          >
            {isOwned ? (
              <>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                </svg>
                <span>Owned</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>I own this</span>
              </>
            )}
          </button>
        )}

        {/* Alternatives Toggle */}
        {showAlternatives && hasAlternatives && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-1 px-2 py-1.5 text-neutral-400 hover:text-white text-sm transition-colors ml-auto"
          >
            <span className="truncate">{isExpanded ? 'Hide' : 'Alternatives'}</span>
            <svg
              className={`w-4 h-4 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>

      {/* Alternatives Panel */}
      {showAlternatives && hasAlternatives && isExpanded && (
        <div className="mt-4 pt-4 border-t border-neutral-800 space-y-2">
          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-2">
            Alternatives
          </p>
          {product.alternatives?.map((alt) => (
            <AlternativeItem
              key={alt.product_id}
              alt={alt}
              onSelect={() => handleSwap(alt.product_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
